import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSuperAdminFacilitatorDetail, FacilitatorDetail as Detail } from '../../services/api';
import SuperAdminLayout from './SuperAdminLayout';

const relLabel = (iso?: string | null): string => {
  if (!iso) return 'Never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const days = Math.floor((Date.now() - t) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(iso).toLocaleDateString();
};
const fmtMins = (m?: number | null): string => {
  if (!m) return '0m';
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${Math.round(m % 60)}m` : `${Math.round(m)}m`;
};

/** Facilitator detail on the super-admin surface + assignment funnel (task 151,
 *  ships dark until completion capture — task 146). Artifact styling. */
const SuperAdminFacilitatorDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [d, setD] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin/login', { replace: true });
      return;
    }
    if (!id) return;
    getSuperAdminFacilitatorDetail(token, Number(id))
      .then(setD)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('superAdminToken');
          navigate('/super-admin/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate, id]);

  return (
    <SuperAdminLayout>
      <div className="content">
        <div className="back" onClick={() => navigate('/super-admin/facilitators')}>‹ All facilitators</div>
        {loading ? (
          <p className="hint">Loading…</p>
        ) : error || !d ? (
          <p className="hint" style={{ color: 'var(--crit)' }}>{error || 'Not found.'}</p>
        ) : (
          <>
            <div className="card">
              <div className="prof-head">
                <span className="avatar" style={{ background: 'var(--fac-soft)', color: 'var(--fac)' }}>
                  {(d.name || d.username).slice(0, 2).toUpperCase()}
                </span>
                <div className="meta">
                  <h3>{d.name || d.username} <span className="pill fac">{d.school}{d.subjects.length ? ` · ${d.subjects.join(', ')}` : ''}</span></h3>
                  <p>@{d.username} · last active {relLabel(d.last_active)}</p>
                </div>
              </div>
            </div>

            <div className="tilerow mt14">
              <div className="tile"><div className="lab">Coach time <span className="ai">approx</span></div><div className="val" style={{ fontSize: '18px' }}>{fmtMins(d.coach_minutes)}</div></div>
              <div className="tile"><div className="lab">Facilitator chats</div><div className="val">{d.coach_chats}</div></div>
              <div className="tile"><div className="lab">Topics completed</div><div className="val">{d.topics_ticked}</div></div>
              <div className="tile"><div className="lab">Lesson plans</div><div className="val">{d.lesson_plans.length}</div></div>
            </div>

            {/* Assignment funnel — task 151 (ships dark until completion capture, task 146) */}
            <div className="card mt14">
              <h3>Assignment funnel</h3>
              <p className="sub">Given → started → completed, this facilitator's classes.</p>
              <div className="fun">
                <div className="frow"><span className="flab">Given</span><span className="bar"><i style={{ width: '100%' }} /></span><span className="n">{d.homeworks_set}</span></div>
                <div className="frow"><span className="flab">Started</span><span className="bar dark" /><span className="n">—</span></div>
                <div className="frow"><span className="flab">Completed</span><span className="bar dark" /><span className="n">—</span></div>
              </div>
              <span className="capture">Completion capture starts once task 146 ships · target 70–90%</span>
            </div>

            <div className="grid g2e mt14">
              <div className="card">
                <h3>Lesson plans</h3>
                {d.lesson_plans.length === 0 ? (
                  <p className="hint">No lesson plans yet.</p>
                ) : (
                  <div className="ivl">
                    {d.lesson_plans.map((lp, i) => (
                      <div key={i} className="iv" style={{ cursor: 'default' }}>
                        <span className="what"><b style={{ fontWeight: 600 }}>{lp.title}</b>
                          <small>{lp.grade != null ? `Grade ${lp.grade}${lp.section ? `-${lp.section}` : ''}` : ''}{lp.subject ? ` · ${lp.subject}` : ''}</small>
                        </span>
                        <span className={`pill ${lp.status === 'pushed' ? 'good' : 'neutral'}`}>{lp.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="card">
                <h3>Coach chats</h3>
                {d.chats.length === 0 ? (
                  <p className="hint">No coach chats yet.</p>
                ) : (
                  <div className="ivl">
                    {d.chats.map((c) => (
                      <div key={c.id} className="iv" style={{ cursor: 'default' }}>
                        <span className="what"><b style={{ fontWeight: 600 }}>{c.title}</b></span>
                        <span className="modref">{c.messages.length} msg{c.messages.length === 1 ? '' : 's'} · {relLabel(c.updated_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {d.classes.length > 0 ? (
              <div className="card mt14">
                <h3>Their classes</h3>
                <p className="sub">Jump to a class's per-student understanding.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {d.classes.map((c, i) => {
                    const params = new URLSearchParams({ school: d.school, grade: String(c.grade) });
                    if (c.section) params.set('section', c.section);
                    return (
                      <span key={i} className="pill neutral" style={{ cursor: 'pointer' }} onClick={() => navigate(`/super-admin/class?${params.toString()}`)}>
                        Grade {c.grade}{c.section ? `-${c.section}` : ''} ›
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminFacilitatorDetail;
